/**
 * Bounded-propagation messaging for switches and rollbacks (#1421 P3b).
 *
 * A routing switch commits immediately in PostgreSQL but reaches the gateway
 * fleet through a cache generation publish, so "the switch succeeded" and
 * "every instance is serving the new config" are two different facts. The
 * console has to say both, and has to keep the degraded case visibly apart
 * from the converged one — they call for opposite operator behaviour ("done"
 * vs "wait and re-check before drawing conclusions from traffic").
 */
import type { RoutingCacheConvergence, RoutingSwitchCacheConvergence } from '@nadoutong/contracts';
import { parseTimestampMs } from '../../components/formatters.ts';

/**
 * Worst-case convergence window, in seconds — **for the pre-commit hint only**.
 *
 * Mirrors `ROUTING_CANDIDATE_DEGRADED_AFTER_MS` in
 * `apps/gateway-worker/src/routing/active-assignment.ts`. It is restated here
 * rather than imported because `apps/*` may not import another app's source;
 * promoting it into `packages/contracts` is the real fix and is tracked in
 * #1477.
 *
 * Everything after the switch commits uses {@link convergenceWindowSeconds}
 * against the response's own `effective_after` instead, so the number an
 * operator reads while waiting comes from the gateway that computed it. The
 * drift this avoids is not cosmetic: if the gateway's window moved to 60s and
 * the console still promised 20, an operator at t=25s would conclude the
 * switch had failed and start a rollback inside a still-converging window.
 * Only the hint shown *before* the request — where there is no response to
 * read a deadline from — can still be wrong, and it is wrong in the harmless
 * direction of understating a wait nobody is timing yet.
 */
export const ROUTING_CONVERGENCE_WORST_CASE_SECONDS = 20;

/**
 * Ceiling on the wait this console will quote, in seconds.
 *
 * The number reaching the operator is a duration they will sit through, so it
 * needs an upper bound that is generous against a genuine change to the
 * gateway's window and hostile to arithmetic that has gone wrong. Convergence
 * is a cache-generation publish measured in seconds; five minutes is more room
 * than any plausible increase needs, and far below the ten-minute figure a
 * mis-set workstation clock produces.
 */
export const ROUTING_CONVERGENCE_MAX_REPORTED_SECONDS = 300;

/**
 * How long the operator should expect to wait for the fleet, in seconds.
 *
 * ── Why this does not subtract from `Date.now()` ─────────────────────────
 * `effective_after` is stamped on the gateway's clock. Measuring it against
 * the browser's yields `window + skew`, and skew on an ops workstation is
 * unbounded: ten minutes slow prints "about 620 seconds", and the operator
 * either waits ten minutes inside a twenty-second window or concludes routing
 * is broken and starts a rollback. That is a worse failure than the drift this
 * function was introduced to avoid, and unlike drift it is invisible.
 *
 * Both responses carry a second server-stamped instant — the `switched_at` of
 * the event the switch or rollback just wrote — so the window's real duration
 * is `effective_after - switched_at`, computed entirely on one clock. That is
 * the preferred path and it is exact regardless of what the workstation
 * believes the time is.
 *
 * The remaining cases, in order:
 *
 *  - no `cache`, or an unparseable deadline: nothing to derive from, so the
 *    mirrored {@link ROUTING_CONVERGENCE_WORST_CASE_SECONDS} stands in. A
 *    missing deadline must not print "NaN", and must not print "0" either,
 *    which would read as "already converged".
 *  - a parseable deadline but no usable server-side reference: fall back to
 *    the client clock, but clamp to
 *    {@link ROUTING_CONVERGENCE_MAX_REPORTED_SECONDS}. A parseable-but-wrong
 *    difference is the case that prints a confident number, so it is the one
 *    that needs the ceiling.
 *  - a deadline at or before its reference (a fast publish, or a slow round
 *    trip on the client path): clamp to 1, not 0 — the response said the
 *    publish was partial, so the honest floor is "any moment now".
 */
export function convergenceWindowSeconds(
  cache: RoutingCacheConvergence | null | undefined,
  /** `switched_at` of the event this response wrote; both are gateway-stamped. */
  serverStampedAt: string | null,
  nowMs: number,
): number {
  if (cache === null || cache === undefined) return ROUTING_CONVERGENCE_WORST_CASE_SECONDS;
  const deadlineMs = parseTimestampMs(cache.effective_after);
  if (!Number.isFinite(deadlineMs)) return ROUTING_CONVERGENCE_WORST_CASE_SECONDS;
  const referenceMs = serverStampedAt === null ? Number.NaN : parseTimestampMs(serverStampedAt);
  const skewFree = Number.isFinite(referenceMs);
  const elapsedSeconds = Math.ceil((deadlineMs - (skewFree ? referenceMs : nowMs)) / 1000);
  return Math.min(
    Math.max(1, elapsedSeconds),
    // The skew-free measurement needs no ceiling — it cannot be inflated by a
    // client clock — but capping it too costs nothing and keeps one bound on
    // what this surface will ever ask an operator to wait for.
    ROUTING_CONVERGENCE_MAX_REPORTED_SECONDS,
  );
}

/**
 * `immediate` — every scope published; the fleet is already serving it.
 * `degraded` — the publish was partial; converge within the bounded window.
 * `unknown` — the response carried no convergence at all (an older writer).
 */
export type ConvergenceOutcome = 'immediate' | 'degraded' | 'unknown';

export function convergenceOutcome(
  cache: RoutingCacheConvergence | null | undefined,
): ConvergenceOutcome {
  if (cache === null || cache === undefined) return 'unknown';
  return cache.convergence === 'immediate' ? 'immediate' : 'degraded';
}

/**
 * Same verdict for a recorded event, with the extra state only the log has:
 * `pending` means the switch committed and the post-commit annotation never
 * landed. That is "we do not know", and the contract keeps it as its own
 * marker precisely so it cannot be read as "did not converge".
 */
export type EventConvergenceOutcome = ConvergenceOutcome | 'pending';

export function eventConvergenceOutcome(
  cache: RoutingSwitchCacheConvergence | null,
): EventConvergenceOutcome {
  if (cache === null) return 'unknown';
  if (cache.status === 'pending') return 'pending';
  return cache.convergence === 'immediate' ? 'immediate' : 'degraded';
}
